/**
 * Workspace "Verified caller ID" card.
 * Props: api (API.json), toast(message, type).
 * Mount from Settings for tenant_admin (and a super admin using View as).
 */
const { useEffect, useState } = React;

const WHY = 'Carriers check who owns a number. Verified numbers are less likely to show as Spam Likely and get answered more.';

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function emptyForm() {
  return {
    legal_business_name: '',
    business_type: '',
    business_industry: '',
    ein: '',
    website: '',
    street: '',
    city: '',
    region: '',
    postal_code: '',
    rep_first_name: '',
    rep_last_name: '',
    rep_email: '',
    rep_phone: '',
    rep_title: '',
    show_business_name: false,
    cnam_display_name: '',
    voice_integrity_requested: false,
    voice_integrity_use_case: '',
    voice_integrity_employee_count: '',
    voice_integrity_daily_calls: ''
  };
}

function formFromProfile(profile) {
  const row = profile || {};
  return {
    ...emptyForm(),
    legal_business_name: row.legal_business_name || '',
    business_type: row.business_type || '',
    business_industry: row.business_industry || '',
    website: row.website || '',
    street: row.street || '',
    city: row.city || '',
    region: row.region || '',
    postal_code: row.postal_code || '',
    rep_first_name: row.rep_first_name || '',
    rep_last_name: row.rep_last_name || '',
    rep_email: row.rep_email || '',
    rep_phone: row.rep_phone || '',
    rep_title: row.rep_title || '',
    show_business_name: !!row.cnam_display_name,
    cnam_display_name: row.cnam_display_name || '',
    voice_integrity_requested: !!row.voice_integrity_requested,
    voice_integrity_use_case: row.voice_integrity_use_case || '',
    voice_integrity_employee_count: row.voice_integrity_employee_count || '',
    voice_integrity_daily_calls: row.voice_integrity_daily_calls || ''
  };
}

function statusRows(profile) {
  const statuses = (profile && profile.statuses) || {};
  return [
    ['Business profile', statuses.business_profile],
    ['Verified caller ID (STIR/SHAKEN A)', statuses.shaken],
    ['Spam-label registration', statuses.voice_integrity],
    ['Caller name', statuses.cnam]
  ];
}

export function CallerIdSetup({ api, toast }) {
  const [profile, setProfile] = useState(null);
  const [options, setOptions] = useState({
    business_types: [],
    business_industries: [],
    job_positions: [],
    voice_integrity_use_cases: []
  });
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function apply(data) {
    const next = data && data.profile ? data.profile : null;
    setProfile(next);
    if (data && data.options) setOptions(data.options);
    setForm(formFromProfile(next));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api('GET', '/api/compliance/caller-id').then((data) => {
      if (!cancelled) apply(data);
    }).catch((err) => {
      if (!cancelled) setError(errorMessage(err, 'Could not load verified caller ID.'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [api]);

  function notify(message, type) {
    if (toast) toast(message, type);
  }

  function setField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function payload() {
    return {
      ...form,
      show_business_name: form.show_business_name,
      cnam_display_name: form.show_business_name ? form.cnam_display_name : '',
      show_cnam: form.show_business_name,
      register_voice_integrity: form.voice_integrity_requested
    };
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const data = await api('PUT', '/api/compliance/caller-id', payload());
      apply(data);
      notify('Business details saved.');
    } catch (err) {
      const message = errorMessage(err, 'Could not save the business details.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setError('');
    try {
      await api('PUT', '/api/compliance/caller-id', payload());
      const data = await api('POST', '/api/compliance/caller-id/submit', {});
      apply(data);
      notify(data && data.message ? data.message : 'Submitted for review.');
    } catch (err) {
      const message = errorMessage(err, 'Could not submit for review.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    setBusy(true);
    setError('');
    try {
      const data = await api('POST', '/api/compliance/caller-id/refresh', {});
      apply(data);
      notify('Review status updated.');
    } catch (err) {
      const message = errorMessage(err, 'Could not refresh the review status.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <section>
        <h3 className="section-heading">Verified caller ID</h3>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  const locked = !!(profile && profile.byo);
  const einHint = profile && profile.ein_last4 ? `On file: ${profile.ein_last4}. Leave blank to keep it.` : '9 digits';

  return (
    <section>
      <h3 className="section-heading">Verified caller ID</h3>
      <p className="field-help">{WHY}</p>
      {error ? <p className="field-help" role="alert">{error}</p> : null}
      {profile && profile.next_step ? <p className="field-help" role="status">{profile.next_step}</p> : null}
      <p className="field-help">
        {profile && profile.phone_e164
          ? `The number that gets verified is ${profile.phone_e164}.`
          : 'Add a phone number for this workspace before submitting. The shared platform number is not used.'}
      </p>
      <ul className="field-help">
        {statusRows(profile).map(([label, row]) => (
          <li key={label}>{label}: {(row && row.label) || 'Not started'}</li>
        ))}
      </ul>
      <p className="field-help">Twilio reviews this in about 1–3 business days.</p>
      {locked ? null : (
        <form onSubmit={save}>
          <div className="field">
            <label htmlFor="cid-legal-name">Legal business name</label>
            <input id="cid-legal-name" className="input" value={form.legal_business_name} disabled={busy} onChange={(event) => setField('legal_business_name', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-type">Business type</label>
            <select id="cid-type" className="input" value={form.business_type} disabled={busy} onChange={(event) => setField('business_type', event.target.value)}>
              <option value="">Choose…</option>
              {(options.business_types || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cid-industry">Industry</label>
            <select id="cid-industry" className="input" value={form.business_industry} disabled={busy} onChange={(event) => setField('business_industry', event.target.value)}>
              <option value="">Choose…</option>
              {(options.business_industries || []).map((item) => <option key={item} value={item}>{item.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cid-ein">EIN</label>
            <input id="cid-ein" className="input" value={form.ein} autoComplete="off" placeholder={einHint} disabled={busy} onChange={(event) => setField('ein', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-website">Website</label>
            <input id="cid-website" className="input" value={form.website} placeholder="https://" disabled={busy} onChange={(event) => setField('website', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-street">Street</label>
            <input id="cid-street" className="input" value={form.street} disabled={busy} onChange={(event) => setField('street', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-city">City</label>
            <input id="cid-city" className="input" value={form.city} disabled={busy} onChange={(event) => setField('city', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-region">State</label>
            <input id="cid-region" className="input" value={form.region} maxLength={2} placeholder="CA" disabled={busy} onChange={(event) => setField('region', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-postal">Postal code</label>
            <input id="cid-postal" className="input" value={form.postal_code} disabled={busy} onChange={(event) => setField('postal_code', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-rep-first">Authorized representative first name</label>
            <input id="cid-rep-first" className="input" value={form.rep_first_name} disabled={busy} onChange={(event) => setField('rep_first_name', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-rep-last">Last name</label>
            <input id="cid-rep-last" className="input" value={form.rep_last_name} disabled={busy} onChange={(event) => setField('rep_last_name', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-rep-email">Email</label>
            <input id="cid-rep-email" className="input" type="email" value={form.rep_email} disabled={busy} onChange={(event) => setField('rep_email', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-rep-phone">Phone</label>
            <input id="cid-rep-phone" className="input" value={form.rep_phone} disabled={busy} onChange={(event) => setField('rep_phone', event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cid-rep-title">Job position</label>
            <select id="cid-rep-title" className="input" value={form.rep_title} disabled={busy} onChange={(event) => setField('rep_title', event.target.value)}>
              <option value="">Choose…</option>
              {(options.job_positions || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cid-cnam">
              <input id="cid-cnam" type="checkbox" checked={form.show_business_name} disabled={busy} onChange={(event) => setField('show_business_name', event.target.checked)} />
              {' '}Show business name on calls (landlines)
            </label>
            {form.show_business_name ? (
              <input className="input" maxLength={15} value={form.cnam_display_name} placeholder="Up to 15 characters" disabled={busy} onChange={(event) => setField('cnam_display_name', event.target.value)} />
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="cid-voice">
              <input id="cid-voice" type="checkbox" checked={form.voice_integrity_requested} disabled={busy} onChange={(event) => setField('voice_integrity_requested', event.target.checked)} />
              {' '}Register to reduce spam labels
            </label>
          </div>
          {form.voice_integrity_requested ? (
            <React.Fragment>
              <div className="field">
                <label htmlFor="cid-use">What these calls are for</label>
                <select id="cid-use" className="input" value={form.voice_integrity_use_case} disabled={busy} onChange={(event) => setField('voice_integrity_use_case', event.target.value)}>
                  <option value="">Choose…</option>
                  {(options.voice_integrity_use_cases || []).map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="cid-employees">People who work there</label>
                <input id="cid-employees" className="input" value={form.voice_integrity_employee_count} disabled={busy} onChange={(event) => setField('voice_integrity_employee_count', event.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="cid-daily">Calls on a typical business day</label>
                <input id="cid-daily" className="input" value={form.voice_integrity_daily_calls} disabled={busy} onChange={(event) => setField('voice_integrity_daily_calls', event.target.value)} />
              </div>
            </React.Fragment>
          ) : null}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="button small" type="submit" disabled={busy}>Save</button>
            <button className="button primary small" type="button" disabled={busy} onClick={submit}>Submit for review</button>
            <button className="button small" type="button" disabled={busy || !(profile && profile.submitted_at)} onClick={refresh}>Refresh status</button>
          </div>
        </form>
      )}
    </section>
  );
}
